import { type TFunction } from 'i18next';

import { type Prize, WHEEL_PRIZES, WheelMode } from '@/entities/wheel/model/wheel.types';

import { formatDuration } from '@/shared/helpers/format-duration';
import { formatNumber } from '@/shared/helpers/format-number';
import { formatToPercentage } from '@/shared/helpers/formatToPercentage';
import { type AnyIcon } from '@/shared/ui/icon';
import { XPIcon } from '@/shared/ui/xp-icon/XPIcon';

import { ReactComponent as Booster } from '@/assets/images/boosters/boost-regular-orng-red.svg?react';
import { ReactComponent as ClickerIcon } from '@/assets/images/clicker.svg?react';
import { ReactComponent as Luck } from '@/assets/images/luck.svg?react';
import { ReactComponent as Premium } from '@/assets/images/market/premium-flat.svg?react';
import { ReactComponent as Playcoin } from '@/assets/images/playcoin-s.svg?react';
import { ReactComponent as Ton } from '@/assets/images/ton.svg?react';
import { ReactComponent as Notcoin } from '@/assets/images/wallet/notcoin.svg?react';
import { ReactComponent as Ticket } from '@/assets/images/wallet/ticket.svg?react';

export const PRIZE_CONFIG_BY_TYPE: Record<
  WHEEL_PRIZES,
  {
    name: (t: TFunction, prize: Prize) => string;
    icon?: (prize: Prize) => AnyIcon;
  }
> = {
  [WHEEL_PRIZES.PLAYCOIN]: {
    name: (t, prize) => formatNumber(prize.amount, t),
    icon: () => Playcoin,
  },
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: {
    name: (_, prize) => `x${prize.amount}`,
    icon: () => Booster,
  },
  [WHEEL_PRIZES.BOOSTER_XP]: {
    name: (_, prize) => `x${prize.amount}`,
    icon: () => Booster, // Assuming this is the correct icon for BoosterXP
  },
  [WHEEL_PRIZES.NOT]: {
    name: (t, prize) => formatNumber(prize.amount, t),
    icon: () => Notcoin,
  },
  [WHEEL_PRIZES.TON]: {
    name: (t, prize) => formatNumber(prize.amount, t),
    icon: () => Ton,
  },
  [WHEEL_PRIZES.TICKET]: {
    name: (t, prize) => formatNumber(prize.amount, t),
    icon: () => Ticket,
  },
  [WHEEL_PRIZES.PREMIUM]: {
    name: (_, prize) => `${formatToPercentage(prize.amount)} Telegram Premium`,
    icon: () => Premium,
  },
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: {
    name: (t, prize) =>
      t('Tap Yeti for 10 secs', {
        duration: formatDuration(+(prize.durationSeconds ?? 0), t, {
          withUnit: true,
        }),
      }),
    icon: () => ClickerIcon,
  },
  [WHEEL_PRIZES.CLICKER_XP]: {
    name: (t, prize) =>
      t('Tap Yeti for 10 secs', {
        duration: formatDuration(+(prize.durationSeconds ?? 0), t, {
          withUnit: true,
        }),
      }),
    icon: () => ClickerIcon,
  },
  [WHEEL_PRIZES.LOOTBOX]: {
    name: (_, prize) => prize.name,
  },
  [WHEEL_PRIZES.PARTNER]: {
    name: (_, prize) => prize.name,
    icon: () => XPIcon,
  },
  [WHEEL_PRIZES.XP]: {
    name: (_, prize) => prize.name,
    icon: () => XPIcon,
  },
  [WHEEL_PRIZES.LUCK]: {
    name: (t) => t('Luck'),
    icon: () => Luck,
  },
  [WHEEL_PRIZES.PHYSICAL]: {
    name: (_, prize) => prize.name,
    icon: (prize: Prize) => prize.image,
  },
};

export const MIN_TICKETS_COUNT_TO_SPIN = 1;

export const MIN_TICKETS_COUNT_TO_SPIN_BY_MODE: Record<WheelMode, number> = {
  [WheelMode.SPIN]: 1,
  [WheelMode.SPIN_10]: 10,
};

export const MAX_DAILY_ADS_TICKETS = 3;
export const WHEEL_ADS_COUNTER_KEY = 'wheel_ads_counter';
export const WHEEL_ADS_DATE_KEY = 'wheel_ads_date';

export const WHEEL_PRIZE_ICONS: Record<
  Exclude<
    WHEEL_PRIZES,
    | WHEEL_PRIZES.PHYSICAL
    | WHEEL_PRIZES.PLAYCOIN
    | WHEEL_PRIZES.PARTNER
    | WHEEL_PRIZES.LOOTBOX
  >,
  { image: string | React.ComponentType<{ className?: string }> }
> = {
  [WHEEL_PRIZES.TICKET]: { image: Ticket },
  [WHEEL_PRIZES.PREMIUM]: { image: Premium },
  [WHEEL_PRIZES.TON]: { image: Ton },
  [WHEEL_PRIZES.NOT]: { image: Notcoin },
  // [WHEEL_PRIZES.LOOTBOX]: { image: Lootbox },
  [WHEEL_PRIZES.BOOSTER_PLAYCOIN]: { image: Booster },
  [WHEEL_PRIZES.BOOSTER_XP]: { image: Booster },
  [WHEEL_PRIZES.CLICKER_PLAYCOIN]: { image: ClickerIcon },
  [WHEEL_PRIZES.CLICKER_XP]: { image: ClickerIcon },
  [WHEEL_PRIZES.XP]: { image: XPIcon },
  [WHEEL_PRIZES.LUCK]: { image: Luck },
};
